import React, { useState, useEffect } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { useTheme } from '../../context/PortalThemeContext';
import '../returns/OrderSummary.css';
import { ScreenSize } from './ScreenSize';
import { PiEyesFill, PiMapPin, PiShield } from "react-icons/pi";
import { ItemCard } from './ProductVariantCards';
import { fetchWithSessionToken } from './fetchWithSessionToken';
import { fetchDynamicOrder } from '../../utils/orderLookupApi';
import { CHAT_API_BASE } from '../../config/api';
import { ImageContainer } from './ImageContainer';
import { FullscreenPopup } from './FullscreenPopup';
import { fallbackString } from './FallbackUtils';
import { TrackingStatus } from '../returns/TrackingStatus';
import SummaryItemRow from './SummaryItemRow';

const OrderSummary = ({
    orderDetails,
    updateOrderDetails,
    customer,
    trackingInfo,
    onStepChange,
    shopifyProducts,
    setSelectedReturn,
    // New props for customization
    mode = 'returns', // 'returns' or 'claims'
    buttonText = 'Start Return or Exchange',
    onButtonClick,
    isButtonDisabled = false,
    buttonLoadingText = 'Your return has been submitted',
    // Return-specific props (only used when mode === 'returns')
    orderReturns = [],
    orderClaims = [],
    ReturnStatus = null,
    ReturnPolicyLink = null,
    // Claim-specific props (only used when mode === 'claims')
    claimDetails = null,
    onCancelClaim = null,
}) => {
    const theme = useTheme();
    const [isLoading, setIsLoading] = useState(false);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [returnToCancel, setReturnToCancel] = useState(null);
    const [order, setOrder] = useState(orderDetails);
    const [showLabelModal, setShowLabelModal] = useState(false);
    const [selectedReturnForModal, setSelectedReturnForModal] = useState(null);
    const [labelToCopy, setLabelToCopy] = useState('');
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const handleResize = () => setScreenWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Auto-load order from query parameters if they exist
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const urlOrderNumber = urlParams.get('orderNumber');
        const urlEmail = urlParams.get('email');
        const autoSearch = urlParams.get('autoSearch') === 'true';

        if (autoSearch && urlOrderNumber && urlEmail) {
            // console.log('OrderSummary: Auto-loading order from query params:', { urlOrderNumber, urlEmail });
            autoLoadOrder(urlOrderNumber, urlEmail);
        }
    }, []);

    const autoLoadOrder = async (orderNumber, email) => {
        setIsLoading(true);

        try {
            const shopId = theme.store;
            const response = await fetchDynamicOrder({
                companyId: shopId,
                searchParam: orderNumber,
                additionalParam: email || orderDetails?.shipping_address?.zip || orderDetails?.shipping_address?.phone,
            });

            if (!response.ok) {
                console.error('Failed to auto-load order');
                setIsLoading(false);
                return;
            }

            const ordersJson = await response.json();
            const orders = ordersJson.orders;

            if (!orders || orders.length === 0) {
                console.error('No orders found for auto-load');
                setIsLoading(false);
                return;
            }

            const orderData = order;

            if (!orderData.customer && Array.isArray(orderData.orders) && orderData.order?.customer) {
                orderData.customer = orderData.order.customer;
            }

            if (ordersJson.bearerToken && orderData.order_number) {
                localStorage.setItem('sessionToken', ordersJson.bearerToken);
                localStorage.setItem('orderId', orderData.order_number);
                localStorage.setItem('shopId', shopId);
                localStorage.setItem('userEmail', email);
            }

            orderData.orderReturns = ordersJson.orderReturns;
            orderData.orderClaims = ordersJson.orderClaims;

            // Update the order details
            if (updateOrderDetails) {
                updateOrderDetails(orderData);
            }

            // Clear query parameters and refresh the page
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);

            // Simple refresh to ensure proper component transition
            setTimeout(() => {
                window.location.reload();
            }, 100);

        } catch (err) {
            console.error('Auto-load order error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const totalShipping = Array.isArray(orderDetails?.shipping_lines)
        ? orderDetails.shipping_lines.reduce((sum, line) => {
            const price = parseFloat(line?.price || "0.00");
            return sum + (isNaN(price) ? 0.00 : price);
        }, 0.00)
        : 0.00;

    const totalShippingFormatted = totalShipping.toFixed(2);

    // Build orderData with all details from the order
    const orderData = {
        orderId: orderDetails.order_number || orderDetails.orderNumber,
        customer: orderDetails.customer,
        shopId: orderDetails.shopId || orderDetails.companyId,
        items: orderDetails?.line_items,
        currentTotalTax: orderDetails?.current_total_tax,
        protectionAmount: orderDetails?.shippingProtection,
        currentTotalDiscounts: orderDetails?.current_total_discounts,
        currentTotalShipping: totalShipping.toFixed(2),
        orderDate: new Date(orderDetails.created_at || orderDetails.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
        }),
        trackingStatus: trackingInfo?.status ? TrackingStatus[trackingInfo?.status] : TrackingStatus.NOT_FOUND,
        trackingInfo: orderDetails.trackingInfo,
        totalPrice: orderDetails.totalPrice,
        hasBeenReturned: orderDetails.hasBeenReturned ?? false,
    };

    const isReturnEligible = orderData.trackingStatus === TrackingStatus.DELIVERED;
    const trackStatusBkgd =
        orderData.trackingStatus === TrackingStatus.DELIVERED
            ? "bg-success"
            : orderData.trackingStatus === TrackingStatus.IN_TRANSIT
                ? "bg-warning"
                : "bg-danger";
    const trackStatusTextColor = orderData.trackingStatus === TrackingStatus.IN_TRANSIT ? "var(--text-dark)" : "white"

    // Return-specific functions (only used when mode === 'returns')
    const refreshOrderDetails = async (orderId, shopId, customerEmail, updateOrderDetails) => {
        if (mode !== 'returns') return;

        try {
            const response = await fetchDynamicOrder(
                {
                    companyId: shopId,
                    searchParam: orderId,
                    additionalParam: customerEmail || orderDetails?.shipping_address?.zip || orderDetails?.shipping_address?.phone,
                },
                { headers: { 'X-User-Email': customerEmail } }
            );

            if (!response.ok) throw new Error('Failed to fetch order');

            const data = await response.json();
            const enrichedOrder = data.order;

            enrichedOrder.orderId = orderId;
            enrichedOrder.orderReturns = data.orderReturns;
            enrichedOrder.orderClaims = data.orderClaims;
            enrichedOrder.trackingInfo = data.trackingInfo;
            enrichedOrder.shopifyProducts = data.shopifyProducts;
            enrichedOrder.bearerToken = data.bearerToken;

            localStorage.setItem('brella_order', JSON.stringify(enrichedOrder));
            updateOrderDetails(enrichedOrder);
        } catch (err) {
            console.error('Error refreshing order:', err);
        }
    };

    const cancelReturn = async (ret) => {
        if (mode !== 'returns' || !ret || ret.returnStatus === ReturnStatus.CANCELLED || ret.returnStatus === ReturnStatus.CUSTOMER_CANCELLED) return;

        setIsLoading(true);

        try {
            const requestUrl = new URL(`${CHAT_API_BASE}/companies/orders/customer/tracking/${ret.trackingIdToStore}`);
            requestUrl.searchParams.set('shopId', orderData.shopId);
            requestUrl.searchParams.set('returnId', ret.id);

            const response = await fetchWithSessionToken(requestUrl.toString(), {
                method: 'DELETE',
            });

            if (!response.ok) throw new Error('Failed to cancel return');

            const updatedReturns = orderDetails.orderReturns.map(r =>
                r._id === ret._id ? { ...r, returnStatus: ReturnStatus.CUSTOMER_CANCELLED } : r
            );

            orderDetails.orderReturns = updatedReturns;
            setSelectedReturn(null);
            await refreshOrderDetails(orderDetails.order_number, orderDetails.shopId, customer.email, updateOrderDetails);

        } catch (error) {
            console.error('Cancel return failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const reopenReturn = async (ret) => {
        if (mode !== 'returns' || !ret || ret.returnStatus !== ReturnStatus.CUSTOMER_CANCELLED) return;

        setIsLoading(true);

        try {
            const requestUrl = new URL(`${CHAT_API_BASE}/companies/${orderData.shopId}/returns/${ret.id}/reopen`);

            const response = await fetchWithSessionToken(requestUrl.toString(), {
                method: 'POST',
            });

            if (!response.ok) throw new Error('Failed to reopen return');

            const updatedReturns = orderDetails.orderReturns.map(r =>
                r._id === ret._id ? { ...r, returnStatus: r.shippingStatus === "NONE" ? ReturnStatus.REQUESTED : ReturnStatus.PROCESSING } : r
            );

            orderDetails.orderReturns = updatedReturns;
            await refreshOrderDetails(orderDetails.order_number, orderDetails.shopId, customer.email, updateOrderDetails);

        } catch (error) {
            console.error('Reopen return failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const downloadReturnLabel = async (ret) => {
        if (mode !== 'returns' || !ret || ret.returnStatus === ReturnStatus.CANCELLED || ret.returnStatus === ReturnStatus.CUSTOMER_CANCELLED) return;

        setIsLoading(true);

        try {
            if (ret.returnService === 'USPS') {
                const brokerId = ret.labelBrokerId ?? '';
                if (!brokerId) throw new Error('No Label Broker ID found');

                setSelectedReturnForModal(ret);
                setLabelToCopy(brokerId);
                setShowLabelModal(true);
                return;
            } else {
                const requestUrl = new URL(`${CHAT_API_BASE}/companies/${orderData.shopId}/returns/label`);
                requestUrl.searchParams.set('companyId', orderData.shopId);
                requestUrl.searchParams.set('orderId', orderData.orderId);

                const response = await fetchWithSessionToken(requestUrl.toString());

                if (!response.ok) throw new Error('Failed to fetch return label');

                const blob = await response.blob();
                const objectUrl = URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = objectUrl;
                link.download = `return_label_${orderData.orderId}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(objectUrl);
            }
        } catch (error) {
            console.error('Error handling return label:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const allReturned =
        mode === 'returns' &&
        Array.isArray(orderReturns) &&
        Array.isArray(orderDetails.line_items) &&
        orderDetails.line_items.every((item) =>
            orderReturns.some((ret) =>
                ret.returnedItems?.some((returned) =>
                    String(returned.itemId) === String(item.id) && (returned.quantity >= item.quantity)
                )
            )
        );

    const allItemsReturned =
        Array.isArray(orderReturns) &&
        Array.isArray(orderDetails.line_items) &&
        orderDetails.line_items.every((item) =>
            orderReturns.some((ret) =>
                ret.returnedItems?.some((returned) =>
                    String(returned.itemId) === String(item.id)
                )
            )
        );

    // Check if all items have claims filed
    const allItemsHaveClaims =
        mode === 'claims' &&
        Array.isArray(orderClaims) &&
        Array.isArray(orderDetails.line_items) &&
        orderDetails.line_items.every((item) =>
            orderClaims.some((claim) =>
                claim.claimedItems?.some((claimed) =>
                    String(claimed.lineItemId) === String(item.id)
                )
            )
        );

    // Main action button
    const ActionButton = (
        <Button
            onClick={onButtonClick}
            className="button-accent px-3 py-2 text-nowrap w-100"
            disabled={isLoading || (mode === 'returns' && allReturned) || (mode === 'claims' && allItemsHaveClaims) || isButtonDisabled}
        >
            {isLoading
                ? <Spinner animation="border" size="sm" />
                : (mode === 'returns' && allReturned)
                    ? buttonLoadingText
                    : (mode === 'claims' && allItemsHaveClaims)
                        ? 'Claim Filed'
                        : buttonText}
        </Button>
    );

    const HeaderWithDetails = ({ headerText, detailTexts }) => (
        <div className='header-detail-item'>
            <div style={{
                fontSize: '1.125rem', fontWeight: 600, marginBottom: '1.125rem'
            }}>
                {headerText}
            </div>
            <div className='medium-text' style={{ gap: '0.5rem' }}>
                {detailTexts.map((text, index) => (
                    <p key={index}>{text}</p>
                ))}
            </div>
        </div>
    );

    const OrderItemRow = ({ item }) => {
        const product = shopifyProducts?.find((p) => p.id === item.product_id);

        return (
            <ItemCard
                item={item}
                cardStyle={false}
                horizontal={true}
                orderDiscount={orderData.currentTotalDiscounts}
                orderTotal={subtotalLineItems}
                flowType={mode === 'claims' ? 'CLAIM' : 'RETURN'}
            />
        );
    };

    const lineItems = orderData.items ?? [];
    const subtotalLineItems = lineItems.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);

    const taxes = Number(orderData?.currentTotalTax ?? 0.00);
    const shipping = Number(orderData?.currentTotalShipping ?? 0.00);
    const discounts = Number(orderData?.currentTotalDiscounts ?? 0.00);
    const subtotal = Number(subtotalLineItems ?? 0.00);

    const total = subtotal + orderData.protectionAmount + taxes + shipping - discounts;
    const totalPrice = orderData.totalPrice;

    return (
        <div
            className="d-flex-column"
            style={{
                backgroundColor: theme.background,
            }}
        >
            <div className='d-flex-row'>
                <div style={{ flex: 2, justifyContent: 'left', alignItems: 'start' }}>
                    <h3 className="order-number">
                        {orderDetails.order_status_url ? (
                            <a
                                href={orderDetails.order_status_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    color: 'inherit',
                                    textDecoration: 'none',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.textDecoration = 'underline';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.textDecoration = 'none';
                                }}
                            >
                                Order #{orderData.orderId}
                            </a>
                        ) : (
                            `Order #${orderData.orderId}`
                        )}
                    </h3>

                    <div className='d-flex-row info-button-block'>
                        <div className='order-subtext'>
                            <span style={{ margin: 0, whiteSpace: 'nowrap' }}>
                                Order Date: {orderData.orderDate}
                            </span>
                            <div className='divider' />
                            <span style={{ margin: 0, whiteSpace: 'nowrap' }}>
                                Delivery status: <span className={`${trackStatusBkgd} px-2 py-1 border border-white rounded-2`} style={{ color: trackStatusTextColor }}>{orderData.trackingStatus}</span>
                            </span>
                        </div>

                        {/* Switch button to here on small screens */}
                        {screenWidth <= ScreenSize.MD && isReturnEligible && ActionButton}
                    </div>
                </div>
                {screenWidth > ScreenSize.MD && isReturnEligible && (
                    <div style={{ flex: '1' }}>
                        {/* Show button in 2nd column only on wide screens */}
                        {ActionButton}
                    </div>
                )}
            </div>

            {/* Return-specific section (only show for returns mode) */}
            {mode === 'returns' && Array.isArray(orderReturns) && orderReturns.length > 0 && (
                <div className='d-flex flex-column gap-3 mt-3'>
                    {orderReturns.map((ret, idx) => {
                        const isCancelled = ret.returnStatus === ReturnStatus.CANCELLED || ret.returnStatus === ReturnStatus.CUSTOMER_CANCELLED;
                        const isCustomerCancelled = ret.returnStatus === ReturnStatus.CUSTOMER_CANCELLED;
                        const returnByDate = ret.returnBy
                            ? new Date(ret.returnBy).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                            })
                            : 'July 25, 2025';
                        const qrCode = ret.brokerBase64
                            ? `data:image/png;base64,${ret.brokerBase64}`
                            : null;
                        // Whether a shipping label was actually generated for this
                        // return. Label generation can be paused server-side, in
                        // which case there is no QR / broker ID / tracking — so we
                        // hide all label-related UI instead of dead-ending on a
                        // download button or "show this QR code" instructions.
                        const hasLabel = Boolean(
                            ret.labelBrokerId || ret.brokerBase64 || ret.trackingId || ret.trackingNumber
                        );

                        return (
                            <div key={idx} className='card d-flex-column flex-md-row w-100 align-items-stretch justify-content-start'
                                style={{ padding: '1rem', gap: '2rem' }}>
                                {isCancelled ? (
                                    <div className='d-flex-column w-100'>
                                        <div className="d-flex flex-row align-items-center gap-5 w-100 justify-content-between">
                                            <div className='card-header-text mb-0 me-3' style={{ fontSize: '1.125rem' }}>
                                                Return Canceled
                                            </div>
                                            {isCustomerCancelled && (
                                            <button
                                                onClick={() => reopenReturn(ret)}
                                                className="button-accent-light"
                                                disabled={isLoading}
                                            >
                                                {isLoading ? (
                                                    <Spinner animation="border" size="sm" />
                                                ) : (
                                                    'Reopen Return'
                                                )}
                                            </button>
                                        )}
                                        </div>

                                        {ret.returnedItems.map((item, index) => (
                                            <div key={item.itemId || index} className="d-flex align-items-center" style={{ marginTop: '0.75rem' }}>
                                                <img
                                                    src={item.itemSrc}
                                                    alt={item.itemName}
                                                    style={{
                                                        width: '50px',
                                                        height: '50px',
                                                        objectFit: 'cover',
                                                        marginRight: '1rem',
                                                        borderRadius: '0.25rem',
                                                        border: '1px solid var(--border-color)'
                                                    }}
                                                />
                                                <div>
                                                    <div style={{ fontWeight: 600 }}>
                                                        {item.itemName}
                                                    </div>
                                                    <div className="small text-muted">Price: ${parseFloat(item.itemPrice || 0).toFixed(2)}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <>
                                        <div className='d-flex-column w-100 justify-content-between' style={{ flex: 2 }}>
                                            <div>
                                                <div className='card-header-text'>
                                                    Return Started
                                                </div>
                                                <div className='medium-text' style={{ marginTop: '1rem' }}>
                                                    <p>1. Pack your items</p>
                                                    {hasLabel ? (
                                                        <p>2. Attach the provided label or show the QR code at a USPS Label Broker location by {returnByDate}</p>
                                                    ) : (
                                                        <p>2. Ship your items back by {returnByDate}</p>
                                                    )}
                                                    <p>3. We'll issue your refund or exchange once we receive your return</p>
                                                </div>
                                            </div>

                                            <div>
                                                <Button
                                                    onClick={() => {
                                                        setSelectedReturn(ret);
                                                        onStepChange('RETURN_TRACKING');
                                                    }}
                                                    className="button-accent-light"
                                                    disabled={isCancelled || isLoading}
                                                    style={{ marginTop: '1.5rem', justifySelf: 'flex-end' }}
                                                >
                                                    {isLoading ? (
                                                        <Spinner animation="border" size="sm" />
                                                    ) : (
                                                        <span className="d-flex justify-content-center align-items-center gap-2">
                                                            <span>Track Return</span>
                                                            <PiEyesFill size={24} />
                                                        </span>
                                                    )}
                                                </Button>

                                                <div
                                                    className={`text-button ${isCancelled ? 'disabled' : ''}`}
                                                    style={{
                                                        marginTop: '1rem',
                                                        fontWeight: 500,
                                                    }}
                                                    onClick={() => {
                                                        setReturnToCancel(ret);
                                                        setShowCancelModal(true);
                                                    }}
                                                >
                                                    {isCancelled ? 'Return Canceled' : 'Cancel this return'}
                                                </div>
                                            </div>
                                        </div>

                                        {hasLabel && (
                                        <div className='d-flex-column align-items-center w-100' style={{ flex: 1, height: '100%' }}>
                                            <FullscreenPopup className='item-image-container' trigger={
                                                <ImageContainer imageUrl={qrCode} alt="Return QR Code" />
                                            } fullscreenContent={
                                                <ImageContainer imageUrl={qrCode} alt="Return QR Code" style={{ height: '100vh', width: '100vh' }} />
                                            } />

                                            <Button
                                                onClick={() => {
                                                    const zip = ret?.returnFromAddress?.zip || '';
                                                    const locatorUrl = `https://tools.usps.com/find-location.htm?locationType=po&address=${zip}&filters=LBRORETAIL`;
                                                    window.open(locatorUrl, '_blank');
                                                }}
                                                disabled={isCancelled}
                                                className="button-accent w-100"
                                                style={{ marginTop: '1rem' }}
                                            >
                                                <span className="d-flex justify-content-center align-items-center gap-2 text-nowrap">
                                                    <span>Find USPS Location Near You</span>
                                                    <PiMapPin size={24} />
                                                </span>
                                            </Button>

                                            <div
                                                onClick={() => downloadReturnLabel(ret)}
                                                className={`text-button w-100${isCancelled ? ' disabled' : ''}`}
                                                style={{
                                                    marginTop: '1rem',
                                                    fontWeight: '500',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {isLoading ? (
                                                    <Spinner animation="border" size="sm" />
                                                ) : (
                                                    <>Download return label</>
                                                )}
                                            </div>
                                        </div>
                                        )}
                                    </>
                                )
                                }
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Claims section - show all claims for the order */}
            {(() => {
                return Array.isArray(orderClaims) && orderClaims.length > 0 && (
                    <div className='d-flex flex-column gap-3 w-100 mt-3'>
                        {orderClaims.map((claim, idx) => {
                            const isLegacyClaim = !claim.claimedItems || claim.claimedItems.length === 0;
                            const claimDate = claim.createdAt
                                ? new Date(claim.createdAt).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: '2-digit',
                                })
                                : 'N/A';

                            return (
                                <div key={idx} className='card d-flex-column flex-md-row w-100 align-items-stretch justify-content-start'
                                    style={{ padding: '1rem', gap: '2rem' }}>
                                    <div className='d-flex-column w-100 justify-content-between' style={{ flex: 2 }}>
                                        <div>
                                            <div className='d-flex justify-content-between align-items-start' style={{ marginBottom: '1rem' }}>
                                                <div className='card-header-text'>
                                                    Claim #{claim.id || idx + 1}
                                                </div>
                                            </div>
                                            <div className='medium-text' style={{ marginTop: '1rem' }}>
                                                {claim.totalClaimValue && (
                                                    <p><strong>Total Value:</strong> ${claim.totalClaimValue.toFixed(2)}</p>
                                                )}
                                            </div>

                                            {/* Show claimed items */}
                                            {claim.claimedItems.map((item, itemIdx) => (
                                                <div key={itemIdx} className="d-flex align-items-center" style={{ marginTop: '0.75rem' }}>
                                                    <img
                                                        src={item.productSrc || item.itemImageUrls?.[0]}
                                                        alt={item.productName}
                                                        style={{
                                                            width: '50px',
                                                            height: '50px',
                                                            objectFit: 'cover',
                                                            marginRight: '1rem',
                                                            borderRadius: '0.25rem',
                                                            border: '1px solid var(--border-color)'
                                                        }}
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                    <div style={{
                                                        width: '50px',
                                                        height: '50px',
                                                        backgroundColor: 'var(--disabled-background)',
                                                        borderRadius: '0.25rem',
                                                        marginRight: '1rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '12px',
                                                        color: 'var(--text-light)',
                                                        display: 'none'
                                                    }}>
                                                        IMG
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 600 }}>
                                                            {item.productName}
                                                        </div>
                                                        {item.itemDescription && (
                                                            <div className="small text-muted" style={{ marginTop: '0.25rem' }}>
                                                                {item.itemDescription}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}

                                            {/* Show images if available */}
                                            {claim.imageUrls && claim.imageUrls.length > 0 && (
                                                <div style={{ marginTop: '1rem' }}>
                                                    <div className="small text-muted mb-2">Images submitted:</div>
                                                    <div className="d-flex gap-2" style={{ flexWrap: 'wrap' }}>
                                                        {claim.imageUrls.map((imageUrl, imgIndex) => (
                                                            <div
                                                                key={imgIndex}
                                                                style={{
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    borderRadius: '4px',
                                                                    overflow: 'hidden',
                                                                    border: '1px solid var(--border-color)'
                                                                }}
                                                            >
                                                                <img
                                                                    src={imageUrl}
                                                                    alt={`Claim image ${imgIndex + 1}`}
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        objectFit: 'cover'
                                                                    }}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                        </div>
                                    </div>

                                    <div className='d-flex flex-column align-items-center justify-content-between w-100' style={{ flex: 1 }}>
                                        <div style={{
                                            backgroundColor: 'var(--accent-color-light)',
                                            borderRadius: '8px',
                                            padding: '.5rem',
                                            width: '100%',
                                            textAlign: 'center',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: '0.25rem'
                                        }}>
                                            <div style={{
                                                fontWeight: 700,
                                                fontSize: '14px',
                                                color: theme.primary,
                                                textTransform: 'uppercase',
                                            }}>
                                                {claim.reviewStatus === 'NOT_REVIEWED' ? 'In Review' : claim.reviewStatus}
                                            </div>
                                            <div style={{
                                                fontSize: '14px',
                                                color: theme.textLight,
                                            }}>
                                                Submitted {claimDate}
                                            </div>
                                        </div>
                                        {onCancelClaim && claim.reviewStatus === 'NOT_REVIEWED' && (
                                            <Button
                                                onClick={() => onCancelClaim(claim.id)}
                                                className="button-accent align-self-md-end mt-2"
                                                size="sm"
                                                disabled={isLoading}
                                                style={{
                                                    fontSize: '0.875rem',
                                                    fontWeight: 500,
                                                    padding: '0.375rem 0.75rem',
                                                    minWidth: 'auto'
                                                }}
                                            >
                                                Cancel Claim
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            })()}

            <div
                className='d-flex-row'
                style={{
                    backgroundColor: theme.background,
                    marginTop: '1.5rem',
                }}
            >
                {/* Column 1 - Items and Order Summary */}
                <div style={{ flex: '2' }}>
                    {/* Item List Card */}
                    <div
                        className="card"
                        style={{
                            width: '100%',
                            padding: '1rem',
                            position: 'relative',
                        }}
                    >
                        {/* Track Package button in top right */}
                        <div className="d-flex justify-content-end" style={{ marginBottom: '1rem' }}>
                            <Button
                                onClick={() => onStepChange('ORDER_TRACKING')}
                                className="button-accent-light"
                                disabled={isLoading}
                                style={{
                                    whiteSpace: 'nowrap',
                                    padding: '0.5rem 1rem',
                                }}
                            >
                                {isLoading ? <Spinner animation="border" size="sm" /> : 'Track Package'}
                            </Button>
                        </div>

                        <div className="d-flex-column" style={{ gap: '1rem' }}>
                            {(orderData.items || []).map((item, index) => (
                                <div key={index} style={{ width: '100%' }}>
                                    <OrderItemRow item={item} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Order Summary Card */}
                    <div
                        className="card"
                        style={{
                            width: '100%',
                            marginTop: '1.5rem',
                        }}
                    >
                        <h2 className="card-header-text" style={{ marginBottom: '1.5rem' }}>
                            Order Summary
                        </h2>

                        <SummaryItemRow name="Subtotal" amount={subtotal.toFixed(2)} />
                        <SummaryItemRow name="Discounts" amount={orderData.currentTotalDiscounts ?? "0.00"} sign='-' />
                        <SummaryItemRow name="Taxes" amount={orderData.currentTotalTax ?? "0.00"} />
                        <SummaryItemRow name="Shipping" amount={orderData.currentTotalShipping ?? "0.00"} />

                        {orderData.protectionAmount > 0 && (
                            <SummaryItemRow name="Shipping Coverage" amount={orderData.protectionAmount.toFixed(2)} />
                        )}

                        <div className="divider-horizontal" />

                        <SummaryItemRow name="Total" amount={total.toFixed(2)} bold={true} />
                    </div>
                </div>

                {/* Column 2 - Shipping Summary */}
                <div style={{ flex: '1', minWidth: '15rem' }}>
                    <div
                        className="card"
                        style={{
                            width: '100%',
                            justifyContent: 'left',
                        }}
                    >
                        <h2 className='card-header-text'>
                            Shipping
                        </h2>
                        <HeaderWithDetails
                            headerText="Shipping Address"
                            detailTexts={[
                                [orderDetails?.shippingAddress?.first_name || customer?.first_name, orderDetails?.shippingAddress?.last_name || customer?.last_name]
                                    .filter(Boolean).join(' '),
                                orderDetails?.shippingAddress?.address1 || customer?.default_address?.address1 || '',
                                [
                                    orderDetails?.shippingAddress?.city || customer?.default_address?.city,
                                    orderDetails?.shippingAddress?.province_code || customer?.default_address?.province_code,
                                    orderDetails?.shippingAddress?.zip || customer?.default_address?.zip
                                ].filter(Boolean).join(', '),
                                orderDetails?.shippingAddress?.country_code || customer?.default_address?.country_code || ''
                            ].filter(Boolean)}
                        />

                        <HeaderWithDetails
                            headerText="Billing Address"
                            detailTexts={[
                                [orderDetails?.billingAddress?.first_name || customer?.first_name, orderDetails?.billingAddress?.last_name || customer?.last_name]
                                    .filter(Boolean).join(' '),
                                orderDetails?.billingAddress?.address1 || customer?.default_address?.address1 || '',
                                [
                                    orderDetails?.billingAddress?.city || customer?.default_address?.city,
                                    orderDetails?.billingAddress?.province_code || customer?.default_address?.province_code,
                                    orderDetails?.billingAddress?.zip || customer?.default_address?.zip
                                ].filter(Boolean).join(', '),
                                orderDetails?.billingAddress?.country_code || customer?.default_address?.country_code || ''
                            ].filter(Boolean)}
                        />
                        <HeaderWithDetails headerText="Carrier" detailTexts={[
                            trackingInfo?.carrier ?? "Carrier Unknown",
                        ]} />
                    </div>
                </div>
            </div>

            {/* Only show return policy link for returns mode */}
            {mode === 'returns' && ReturnPolicyLink && <ReturnPolicyLink flowType="RETURN" />}
            {mode === 'claims' && ReturnPolicyLink && <ReturnPolicyLink flowType="CLAIM" />}

            {/* Return-specific modals (only for returns mode) */}
            {mode === 'returns' && (
                <>
                    <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered backdrop="static">
                        <Modal.Header closeButton>
                            <Modal.Title>Cancel Return</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            Are you sure you want to cancel this return?
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
                                No, go back
                            </Button>
                            <Button
                                variant="danger"
                                onClick={async () => {
                                    await cancelReturn(returnToCancel);
                                    setShowCancelModal(false);
                                }}
                                disabled={isLoading}
                            >
                                Yes, cancel return
                            </Button>
                        </Modal.Footer>
                    </Modal>

                    <Modal show={showLabelModal} onHide={() => setShowLabelModal(false)} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>Copy Label Broker ID</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>To print your return label at a USPS Label Broker location, copy the following ID and then press Continue to USPS</p>
                            <div
                                style={{
                                    background: 'var(--background-color)',
                                    padding: '0.75rem',
                                    borderRadius: '6px',
                                    fontWeight: 'bold',
                                    wordBreak: 'break-all'
                                }}
                            >
                                {labelToCopy}
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                className="button-accent-light"
                                onClick={() => {
                                    navigator.clipboard.writeText(labelToCopy).then(() => {
                                        setCopied(true);
                                        setTimeout(() => setCopied(false), 1500);
                                    });
                                }}
                            >
                                {copied ? 'Copied!' : 'Copy to Clipboard'}
                            </Button>
                            <Button
                                className="button-accent"
                                onClick={() => {
                                    const locatorUrl = `https://tools.usps.com/label-broker.htm?zip=${selectedReturnForModal?.returnFromAddress?.zip || ''}`;
                                    window.open(locatorUrl, '_blank');
                                    setShowLabelModal(false);
                                }}
                            >
                                Continue to USPS
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </>
            )}
        </div>
    );
};

export default OrderSummary; 